import React, { useState, useRef, useEffect, useCallback } from 'react';

const parsePlace = (place) => {
    const a = place.address || {};
    const line1 = [a.house_number, a.road].filter(Boolean).join(' ');
    const city   = a.city || a.town || a.village || a.hamlet || a.county || '';
    const iso    = a['ISO3166-2-lvl4'] || '';
    const state  = iso.includes('-') ? iso.split('-')[1] : (a.state || '');
    const zip    = a.postcode || '';
    const county = a.county || '';
    return { line1, city, state, zip, county };
};

const debounce = (fn, ms) => {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
};

const SearchIcon = ({ spinning }) => (
    <svg
        width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke={spinning ? '#1B75F0' : '#9ca3af'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ transition: 'stroke 0.2s', flexShrink: 0 }}
    >
        {spinning
            ? <circle cx="12" cy="12" r="9" strokeDasharray="28" strokeDashoffset="10">
                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
              </circle>
            : <>
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </>
        }
    </svg>
);

/**
 * AddressAutocomplete — matches the existing `flat` Input variant styling.
 *
 * Props:
 *   value        – current street address string
 *   onSelect     – ({ line1, city, state, zip }) called when user picks a suggestion
 *   onChange     – (string) called as user types (optional)
 *   label        – field label
 *   required     – shows asterisk
 *   error        – boolean, red border
 *   errorText    – validation error message
 *   placeholder
 *   fullWidth    – adds width:100% to container
 *   variant      – 'flat' (default) | 'rounded' — matches Input component variants
 */
const AddressAutocomplete = ({
    value = '',
    onSelect,
    onChange,
    label,
    required,
    error,
    errorText,
    placeholder = 'Start typing your street address…',
    fullWidth = false,
    variant = 'flat',
}) => {
    const [query, setQuery]             = useState(value);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading]         = useState(false);
    const [open, setOpen]               = useState(false);
    const [focused, setFocused]         = useState(false);
    const containerRef = useRef(null);
    const justSelected = useRef(false);

    useEffect(() => { setQuery(value || ''); }, [value]);

    const fetchSuggestions = useCallback(
        debounce(async (q) => {
            if (!q || q.length < 3) { setSuggestions([]); setOpen(false); return; }
            setLoading(true);
            try {
                const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=us&limit=6&q=${encodeURIComponent(q)}`;
                const res  = await fetch(url, {
                    headers: { 'Accept-Language': 'en', 'User-Agent': 'PayrollNoot/1.0 admin@payrollnoot.com' },
                });
                const data = await res.json();
                const filtered = data.filter(p => p.address?.road || p.address?.pedestrian);
                setSuggestions(filtered);
                setOpen(filtered.length > 0);
            } catch {
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 450),
        []
    );

    const handleChange = (e) => {
        const v = e.target.value;
        justSelected.current = false;
        setQuery(v);
        onChange?.(v);
        fetchSuggestions(v);
    };

    const handleSelect = (place) => {
        const parsed = parsePlace(place);
        justSelected.current = true;
        setQuery(parsed.line1 || place.display_name.split(',')[0]);
        setSuggestions([]);
        setOpen(false);
        onSelect?.(parsed);
    };

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setFocused(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Border color logic matching the existing Input component
    const borderColor = error
        ? '#dc3545'
        : focused
        ? '#1B75F0'
        : '#C9C9C9';

    const inputStyle = variant === 'flat'
        ? {
            backgroundColor: '#f4f4f4',
            borderRadius: 12,
            border: `1.5px solid ${borderColor}`,
            height: 50,
            width: '100%',
            padding: '0 42px 0 14px',
            fontSize: 14,
            color: '#111',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
            fontFamily: 'inherit',
        }
        : {
            backgroundColor: '#fff',
            borderRadius: 50,
            border: `1.5px solid ${borderColor}`,
            height: 50,
            width: '100%',
            padding: '0 42px 0 20px',
            fontSize: 14,
            color: '#111',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
            fontFamily: 'inherit',
        };

    return (
        <div
            ref={containerRef}
            className={`input-container${fullWidth ? ' full' : ''}`}
            style={{ position: 'relative' }}
        >
            {label && (
                <label className="input-label">
                    {label}
                    {required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
                </label>
            )}

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onFocus={() => {
                        setFocused(true);
                        if (suggestions.length > 0 && !justSelected.current) setOpen(true);
                    }}
                    onBlur={() => setFocused(false)}
                    placeholder={placeholder}
                    autoComplete="off"
                    style={inputStyle}
                />
                <span style={{
                    position: 'absolute', right: 13, top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex', alignItems: 'center', pointerEvents: 'none',
                }}>
                    <SearchIcon spinning={loading} />
                </span>
            </div>

            {/* Dropdown */}
            {open && suggestions.length > 0 && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    background: '#fff',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                    zIndex: 9999,
                    overflow: 'hidden',
                }}>
                    {suggestions.map((place, i) => {
                        const p = parsePlace(place);
                        const street = p.line1 || place.display_name.split(',')[0];
                        const sub    = [p.city, p.state, p.zip].filter(Boolean).join(', ');
                        return (
                            <div
                                key={i}
                                onMouseDown={() => handleSelect(place)}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 10,
                                    padding: '11px 14px',
                                    cursor: 'pointer',
                                    borderBottom: i < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                            >
                                {/* Pin icon */}
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                    stroke="#1B75F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ flexShrink: 0, marginTop: 2 }}
                                >
                                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{street}</div>
                                    {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sub}</div>}
                                </div>
                            </div>
                        );
                    })}
                    <div style={{
                        padding: '5px 14px', fontSize: 10, color: '#c4c4c4',
                        background: '#fafafa', textAlign: 'right', borderTop: '1px solid #f3f4f6',
                    }}>
                        Powered by OpenStreetMap
                    </div>
                </div>
            )}

            {errorText && <p className="error-text">{errorText}</p>}
        </div>
    );
};

export default AddressAutocomplete;
